import Hat from './hat'
import {
	game
} from "../../Game";
import * as PIXI from 'pixi.js';
import { Settings } from '../../Settings';
import * as AudioManager from '../../utils/AudioManager';
import * as TutorialManager from '../../utils/TutorialManager';
import { b2AddVec2, b2CloneVec2, b2LinearStiffness, b2SubVec2 } from '../../../libs/debugdraw';
import {
	getPortalRopeVisualRoute,
	mapPointBackThroughCrossings,
	mapPointThroughPortal,
	rayCastThroughPortals,
} from '../guns/portalPhysicsQuery';
import {
	portalApertureGuidePoint,
	portalNormal,
	portalTangentDistance,
	portalTransformAngle,
	resolvePortalApertureGuideTangent,
	signedPortalDistance,
} from '../guns/portalMath';

const { getPointer, NULL } = Box2D;

const ANIMATION_TRAVEL_SPEED = 4000 / Settings.PTM;
// Leave a small amount of room between the rope and the solid portal cap. The
// physical anchor sits just behind the cross-line so the portal recognises the
// joint as a far-side rope instead of a static-world anchor blocking transit.
const PORTAL_ROPE_GUIDE_INSET = 0.15;
const PORTAL_ROPE_GUIDE_BACK_OFFSET = 0.05;
const PORTAL_ROPE_GUIDE_RELEASE_HYSTERESIS = 0.2;

const vec1 = new Box2D.b2Vec2();
const vec2 = new Box2D.b2Vec2();
const vec3 = new Box2D.b2Vec2();
const copyVec1 = new Box2D.b2Vec2();

export class RopeHat extends Hat {
	constructor(character, head, body) {
		super(character, head, body);
		this.texture = 'RopeHelmet0000';
		this.hatOffsetLength = 45;
		this.hatOffsetAngle = Math.PI/2;
		this.minRopeLength = 7;

		this.ropeFired = false;
		this.showAnimation = false;
		this.ropeAttached = false;
		this.ropeGoingOut = false;
		this.targetAnimationPoint = null;
		this.currentAnimationPoint = null;
		this.ropeActive = false;
		this.pulleyJoint = null;
		this.pulleyFrameJoint = null;
		this.frameJoint = null;
		this.bendRopeLength = 0;
		this.ropePoints = [];
		this.bendPoint = null;
		this.bendSpeed = null;
		this.bendBody = null;
		this.tilingSprites = [];
		this.isRopeHat = true;
		this.touchedBodies = [];
		this.endRope = null;
		// The linked portal pair a hooked rope passes through, if any.
		this.portalPair = null;
		this.portalRopeRoute = null;
		this.portalTransitRebased = false;
		// The hook animation continues on the far side of that portal.
		this.animationCrossing = null;
		this.animationPhase = 'near';
		this.animationFarAngle = 0;
		this.attach();
	}
	attach(){
		TutorialManager.showTutorial(TutorialManager.TUTORIALS.ROPEHELMET);
		super.attach();
	}
	activate() {
		if (this.ropeFired){
			this.detachRope();
			return;
		}

		if(!this.anchorTexture){
			this.ropeFired = true;
			const rayStart = this.head.GetPosition();
			const angle = this.head.GetAngle() - Math.PI / 2;
			const rayEnd = vec1;
			const length = 100;
			rayEnd.set_x(rayStart.get_x() + length * Math.cos(angle));
			rayEnd.set_y(rayStart.get_y() + length * Math.sin(angle));
			// The hook flies through linked portals and lands on the far side.
			const hit = rayCastThroughPortals(Box2D, this.head.GetWorld(), rayStart, rayEnd, {
				acceptFixture: fixture => !fixture.GetBody().mainCharacter,
				maxCrossings: 1,
				includeMissTrace: true,
			});


			this.anchorTexture = new PIXI.Sprite(PIXI.Texture.from("RopePartAndHook0000"));
			this.anchorTexture.pivot.set(this.anchorTexture.width/2, this.anchorTexture.height / 2);
			this.hatBody.myTexture.parent.addChildAt(this.anchorTexture, this.hatBody.myTexture.parent.getChildIndex(this.hatBody.myTexture));
			this.anchorTexture.rotation = angle;

			this.ropeGoingOut = true;

			const hitBody = hit?.fixture?.GetBody();
			const crossing = hit?.crossings[0] || null;
			// Beyond a portal the hook only holds on static geometry: a joint cannot
			// span two portal frames, but a static anchor is just a world point, so
			// it is pinned where it appears from this side of the portal.
			const holds = Boolean(hitBody) && (!crossing ||
				(hitBody.GetType() === Box2D.b2_staticBody && !hitBody.isPortalSurface));
			this.animationCrossing = null;
			this.animationPhase = 'near';
			if(crossing){
				this.targetAnimationPoint = new Box2D.b2Vec2(
					crossing.entryPoint.x,
					crossing.entryPoint.y,
				);
				this.animationCrossing = {
					entryPoint: crossing.entryPoint,
					exitPoint: crossing.exitPoint,
					anchorReal: hit.point,
				};
				this.animationFarAngle = angle + portalTransformAngle(
					crossing.source.body.GetAngle(),
					crossing.target.body.GetAngle(),
				);
			}
			if (holds && crossing) {
				this.portalPair = {a: crossing.source, b: crossing.target};
				this.portalRopeRoute = {
					entrance: crossing.source,
					exit: crossing.target,
					anchorBody: hitBody,
					anchorReal: {x: hit.point.x, y: hit.point.y},
					guideTangent: null,
					rebased: false,
				};
				this.portalTransitRebased = false;
				const virtualAnchor = mapPointBackThroughCrossings(hit.point, hit.crossings);
				const anchor = new Box2D.b2Vec2(virtualAnchor.x, virtualAnchor.y);
				this.attachRope(anchor, hitBody, true);
				Box2D.destroy(anchor);
				this.ropeAttached = true;

			} else if (holds) {
				const hitPoint = new Box2D.b2Vec2(hit.point.x, hit.point.y);
				this.targetAnimationPoint = b2CloneVec2(hitPoint);
				this.attachRope(hitPoint, hitBody);
				Box2D.destroy(hitPoint);
				this.ropeAttached = true;

			} else {
				this.ropeFired = false;
				if(!crossing) this.targetAnimationPoint = b2CloneVec2(rayEnd);
				this.ropeAttached = false;
			}
			this.currentAnimationPoint = this.getGunStartPosition();
			this.showAnimation = true;
			AudioManager.playSFX('rope-out', 0.2, 1.0 + 0.4 * Math.random()-0.2, this.hatBody.GetPosition());

		}
	}
	detachRope() {
		if(!this.ropeFired) return;

		this.ropeFired = false;
		this.blockControls = false;
		this.ropeAttached = false;
		this.portalPair = null;
		this.portalRopeRoute = null;
		this.portalTransitRebased = false;
		this.releaseRope();
		this.clearTilingRope();
		this.ropePoints = [];

		this.showAnimation = true;
		this.ropeGoingOut = false;
		if(this.animationCrossing){
			// Reel the far side back to the portal first, then the near side home.
			this.animationPhase = 'far';
			this.currentAnimationPoint = new Box2D.b2Vec2(
				this.animationCrossing.anchorReal.x,
				this.animationCrossing.anchorReal.y,
			);
			this.targetAnimationPoint = new Box2D.b2Vec2(
				this.animationCrossing.exitPoint.x,
				this.animationCrossing.exitPoint.y,
			);
		}
		AudioManager.playSFX('rope-in', 0.2, 1.0 + 0.4 * Math.random()-0.2, this.hatBody.GetPosition());

		// make sure we dont lose momentum when attached bodies go out of the screen
		this.touchedBodies.forEach(body=> delete body.ignorePhysicsCuller);
		this.touchedBodies.length = 0;


		if(this.character.attachedToVehicle){
			const frame = this.character.mainPrefabClass.lookupObject['frame'];
			if(frame){
				if(this.character.mainPrefabClass.vehicleName === 'FoddyCan'){
					frame.SetFixedRotation(true);
				}
			}
		}
	}

	attachRope(point, body, precise) {

		this.ropeActive = true;
		this.blockControls = true;

		const bd = new Box2D.b2BodyDef();
		bd.set_type(Box2D.b2_dynamicBody);
		bd.set_angularDamping(0.85);
		bd.set_linearDamping(0.85);

		const farthestPoint = precise ? point : this.findFarthestPointFromBody(0.3, point, body);


		if (farthestPoint === null) return;

		const diff = b2CloneVec2(this.head.GetPosition());
		b2SubVec2(diff, farthestPoint);
		const angle = Math.atan2(diff.y, diff.x) - 90 * game.editor.DEG2RAD;

		bd.get_position().Set(farthestPoint.x, farthestPoint.y);
		bd.set_angle(angle);

		this.ropeEnd = game.editor.CreateBody(bd);
		Box2D.destroy(bd);

		this.ropeEnd.ignorePhysicsCuller = true;
		this.ropeEnd.isRopeEnd = true;

		body.ignorePhysicsCuller = true;
		this.touchedBodies.push(body);

		game.editor.setBodyCollision(this.ropeEnd, 7);
		this.ropeEnd.key = this.head.mySprite.data.prefabInstanceName
		this.ropeEnd.SetBullet(true);

		this.ropeEnd.contactListener = new Box2D.JSContactListener();
		this.ropeEnd.contactListener.BeginContact = ()=>{};
		this.ropeEnd.contactListener.EndContact = ()=>{};
		this.ropeEnd.contactListener.PreSolve = contact => {
			contact.SetEnabled(false);
			// The physical bend strip is a straight segment in the rider's local
			// portal frame. Where that virtual segment meets the entrance wall is
			// not a real obstacle: it continues from the linked portal instead.
			// Treating the host contact as a bend moves the hook to the portal lip,
			// after which the minimum rope length stops the rider short. Keep a
			// portal-routed rope straight until the character has crossed and the
			// rope has been rebuilt entirely on the destination side.
			if(this.portalPair && !this.ropePoints.length) return;
			if (this.bendBody || !this.revoluteJoint || Math.abs(this.revoluteJoint.GetJointSpeed())<0.01) return;
			const bodyA = contact.GetFixtureA().GetBody();
			const bodyB = contact.GetFixtureB().GetBody();
			const targetBody = bodyA === this.ropeEnd ? bodyB : bodyA;
			const targetFixture = bodyA === this.ropeEnd ? contact.GetFixtureB() : contact.GetFixtureA();
			if (targetBody.GetType() !== Box2D.b2_staticBody) return;
			if (!(targetFixture.GetFilterData().get_maskBits() & game.editor.MASKBIT_CHARACTER)) return;
			const worldManifold = new Box2D.b2WorldManifold();
			contact.GetWorldManifold(worldManifold);
			const worldCollisionPoint = worldManifold.get_points(0);
			this.bendPoint = worldCollisionPoint;
			this.bendBody = targetBody;
			this.bendSpeed = this.revoluteJoint.GetJointSpeed();
		}

		//build fixtures
		this.updateRopeFixture();

		const revoluteJointDef = new Box2D.b2RevoluteJointDef();
		revoluteJointDef.Initialize(body, this.ropeEnd, farthestPoint);
		revoluteJointDef.set_collideConnected(true);

		this.revoluteJoint = Box2D.castObject(game.editor.CreateJoint(revoluteJointDef), Box2D.b2RevoluteJoint);
		Box2D.destroy(revoluteJointDef);

		this.setDistanceJointEnabled(true);

		let prismaticJointDef = new Box2D.b2PrismaticJointDef();
		const axis = new Box2D.b2Vec2(Math.cos(this.head.GetAngle() + 90 * game.editor.DEG2RAD), Math.sin(this.head.GetAngle() + 90 * game.editor.DEG2RAD));
		prismaticJointDef.Initialize(this.head, this.ropeEnd, farthestPoint, axis);
		prismaticJointDef.set_maxMotorForce(20000);
		prismaticJointDef.set_enableMotor(false);
		this.pulleyJoint = Box2D.castObject(game.editor.CreateJoint(prismaticJointDef), Box2D.b2PrismaticJoint);
		Box2D.destroy(prismaticJointDef);

		if(this.character.attachedToVehicle){
			const frame = this.character.mainPrefabClass.lookupObject['frame'];
			if(frame){
				prismaticJointDef = new Box2D.b2PrismaticJointDef();
				prismaticJointDef.Initialize(frame, this.ropeEnd, farthestPoint, axis);
				prismaticJointDef.set_maxMotorForce(20000);
				prismaticJointDef.set_enableMotor(false);
				this.pulleyFrameJoint = Box2D.castObject(game.editor.CreateJoint(prismaticJointDef), Box2D.b2PrismaticJoint);


				if(this.character.mainPrefabClass.vehicleName === 'FoddyCan'){
					frame.SetFixedRotation(false);
				}

			}
		}
	}

	setDistanceJointEnabled(enabled){
		if(this.ropeHeadJoint){
			game.editor.DestroyJoint(this.ropeHeadJoint);
		}
		if(this.frameJoint){
			game.editor.DestroyJoint(this.frameJoint);
		}
		if(enabled){
			// A vehicle without a frame, like the Freerunner, hangs from the head.
			const frame = this.character.attachedToVehicle && this.character.mainPrefabClass.lookupObject['frame'];
			if(frame){
				let ropeJointDef = new Box2D.b2DistanceJointDef();
				ropeJointDef.Initialize(frame, this.ropeEnd, frame.GetPosition(), this.ropeEnd.GetPosition());

				const length = ropeJointDef.get_length();
				ropeJointDef.set_minLength(length);
				ropeJointDef.set_maxLength(length);

				ropeJointDef.set_stiffness(0);
				ropeJointDef.set_damping(0);

				this.frameJoint = Box2D.castObject(game.editor.CreateJoint(ropeJointDef), Box2D.b2DistanceJoint);
				Box2D.destroy(ropeJointDef);
			}else{
				let distanceJointDef = new Box2D.b2DistanceJointDef();
				distanceJointDef.Initialize(this.head, this.ropeEnd, this.head.GetPosition(), this.ropeEnd.GetPosition());
				b2LinearStiffness(distanceJointDef, 60, 10, this.head, this.ropeEnd);

				this.ropeHeadJoint = Box2D.castObject(game.editor.CreateJoint(distanceJointDef), Box2D.b2DistanceJoint);
				Box2D.destroy(distanceJointDef);
			}
		}
	}

	releaseRope() {
		if (this.ropeEnd) {
			game.editor.DestroyBody(this.ropeEnd);

			if(this.pulleyJoint) game.editor.DestroyJoint(this.pulleyJoint);
			if(this.pulleyFrameJoint) game.editor.DestroyJoint(this.pulleyFrameJoint);
			this.revoluteJoint = this.pulleyJoint = this.pulleyFrameJoint = null;
			this.ropeActive = false
			this.bendBody = this.bendPoint = null;
			this.ropeEnd = null;
		}
	}
	bendRope(point, body) {
		const diff = vec1;
		diff.Set(this.head.GetPosition().x, this.head.GetPosition().y);
		b2SubVec2(diff, point);

		let angle = Math.atan2(diff.y, diff.x);
		if(this.bendSpeed > 0){
			angle -=  45 * game.editor.DEG2RAD;
		}else{
			angle +=  45 * game.editor.DEG2RAD;
		}

		body.ignorePhysicsCuller = true;
		this.touchedBodies.push(body);

		const offsetPoint = copyVec1;
		offsetPoint.Set(point.x, point.y);
		const offsetLength = 0.5;
		const offset = vec2;
		offset.Set(offsetLength*Math.cos(angle), offsetLength*Math.sin(angle));
		b2AddVec2(offsetPoint, offset);

		const bendPos = vec3;
		bendPos.Set(this.ropeEnd.GetPosition().x, this.ropeEnd.GetPosition().y);
		b2SubVec2(bendPos, offsetPoint).Length();
		const bendLength = bendPos.Length();

		this.bendRopeLength += bendLength;

		this.ropePoints.push({
			point: b2CloneVec2(this.ropeEnd.GetPosition()),
			body: this.revoluteJoint.GetBodyA(),
			speed: this.revoluteJoint.GetJointSpeed(),
			bendLength
		});

		this.releaseRope();

		this.attachRope(offsetPoint, body, true);
		this.oldDir = 0;

	}
	unBendRope() {
		const bendData = this.ropePoints.pop();
		this.bendRopeLength -= bendData.bendLength;

		this.releaseRope();
		this.attachRope(bendData.point, bendData.body, true);
		this.oldDir = 0;
	}
	findFarthestPointFromBody(radius, point, body) {
		const steps = 16;
		const rotChunk = (Math.PI * 2) / steps;

		const callback = Object.assign(new Box2D.JSRayCastCallback(), {
			ReportFixture: function (fixture_p, point_p, normal_p, fraction) {

				const fixture = Box2D.wrapPointer(fixture_p, Box2D.b2Fixture);
				const point = Box2D.wrapPointer(point_p, Box2D.b2Vec2);
				const normal = Box2D.wrapPointer(normal_p, Box2D.b2Vec2);
				if (fixture.GetBody() !== body) return -1;
				this.m_hit = true;
				this.m_point = point;
				this.m_normal = normal;
				this.m_fixture = fixture;
				this.m_fraction = fraction;
			},
			m_hit: false
		});

		let maxLength = 0;
		let farthestPoint = null;

		const dir = vec1;
		dir.Set(this.head.GetPosition().x, this.head.GetPosition().y);
		b2SubVec2(dir, point);
		dir.Normalize();

		for (let i = 0; i < steps; i++) {
			const rot = rotChunk * i;
			const x = radius * Math.cos(rot);
			const y = radius * Math.sin(rot);
			const rayStart = vec2;
			rayStart.Set(point.x + x, point.y + y);
			const radiusEdge = radius*1.1;
			const rayEnd = vec3;
			rayEnd.Set(point.x - dir.x * radiusEdge, point.y - dir.y * radiusEdge);

			this.head.GetWorld().RayCast(callback, rayStart, rayEnd);
			if (callback.m_fraction > maxLength) {
				maxLength = callback.m_fraction;
				farthestPoint = b2CloneVec2(rayStart);
			}
		}
		return farthestPoint;
	}
	clearTilingRope(){
		if(this.tilingSprites.length){
			this.tilingSprites.forEach(tileSprite=>tileSprite.parent.removeChild(tileSprite));
			this.tilingSprites.length = 0;
		}
	}

	getGunStartPosition(){
		const gunStartPosition = b2CloneVec2(this.head.GetPosition());
		const gunAngle = this.hatBody.GetAngle()+90*game.editor.DEG2RAD;
		const gunlength = 3.5;
		gunStartPosition.set_x(gunStartPosition.get_x() - gunlength * Math.cos(gunAngle));
		gunStartPosition.set_y(gunStartPosition.get_y() - gunlength * Math.sin(gunAngle));
		return gunStartPosition;
	}

	updateRopeFixture() {
		if(!this.ropeEnd || this.ropeEnd.destroyed) return;

		this.clearTilingRope();

		const tilingPoints = [];
		this.ropePoints.forEach(rope=>tilingPoints.push(rope.point));
		tilingPoints.push(b2CloneVec2(this.ropeEnd.GetPosition()));

		tilingPoints.push(this.getGunStartPosition());

		// A hook beyond a portal is drawn at its real place on the far side; the
		// near side starts where the rope enters the portal.
		const portalVisualRoute = this.getPortalRopeVisual(
			tilingPoints[0],
			tilingPoints[tilingPoints.length - 1],
		);
		let crossing = null;
		if(portalVisualRoute?.mode === 'direct'){
			this.drawRopeSegment(
				portalVisualRoute.anchorReal,
				portalVisualRoute.gunPoint,
				true,
			);
		}else{
			crossing = portalVisualRoute?.mode === 'split' ?
				portalVisualRoute :
				this.findRopePortalCrossing(tilingPoints[0], tilingPoints[1]);
			if(crossing){
				this.drawRopeSegment(crossing.anchorReal, crossing.exitPoint, true);
				tilingPoints[0] = crossing.entryPoint;
				if(crossing.gunPoint){
					tilingPoints[tilingPoints.length - 1] = crossing.gunPoint;
				}
			}

			for(let i = 1; i<tilingPoints.length; i++){
				this.drawRopeSegment(tilingPoints[i-1], tilingPoints[i], i === 1 && !crossing);
			}
		}
		const fixturesToDestroy = [];
		for (let fixture = this.ropeEnd.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
			fixturesToDestroy.push(fixture);
		}
		fixturesToDestroy.forEach(fixture => this.ropeEnd.DestroyFixture(fixture));

		const ropeDiss = vec1;
		ropeDiss.Set(this.head.GetPosition().x, this.head.GetPosition().y);
		b2SubVec2(ropeDiss, this.ropeEnd.GetPosition());
		const ropeLength = ropeDiss.Length();

		// rope collider
		let fixDef = new Box2D.b2FixtureDef();
		fixDef.set_density(100);
		let shape = new Box2D.b2CircleShape();
		shape.set_m_radius(0.1);

		fixDef.set_shape(shape);

		fixDef.set_isSensor(true);
		this.ropeEnd.CreateFixture(fixDef);
		Box2D.destroy(shape);
		Box2D.destroy(fixDef);

		fixDef = new Box2D.b2FixtureDef();
		fixDef.set_density(0.1);

		const thickness = 0.05;

		// The bend collider only covers the near side of a portal, or it would
		// wrap the rope around the wall the portal sits on.
		let colliderStart = 0;
		if(crossing && !this.ropePoints.length){
			const ropeEndPosition = this.ropeEnd.GetPosition();
			colliderStart = Math.hypot(
				crossing.entryPoint.x - ropeEndPosition.get_x(),
				crossing.entryPoint.y - ropeEndPosition.get_y(),
			) + 0.05;
			colliderStart = Math.min(colliderStart, Math.max(0, ropeLength - 0.2));
		}
		const b2Vec2Arr = [];
		b2Vec2Arr.push({x:-thickness, y:colliderStart});
		b2Vec2Arr.push({x:thickness, y:colliderStart});
		b2Vec2Arr.push({x:0, y:ropeLength});

		shape = new Box2D.b2PolygonShape();
		shape.Set(Box2D.pointsToVec2Array(b2Vec2Arr)[0], b2Vec2Arr.length);
		fixDef.set_shape(shape);
		this.ropeEnd.CreateFixture(fixDef);

		Box2D.destroy(shape);
		Box2D.destroy(fixDef);
	}

	drawRopeSegment(previousPoint, point, placeHook){
		const diff = vec1;
		diff.Set(point.x - previousPoint.x, point.y - previousPoint.y);

		const tilingSprite = new PIXI.TilingSprite({texture: PIXI.Texture.from("rope.png"), width: 4, height: diff.Length()*game.editor.PTM});
		tilingSprite.x = point.x*game.editor.PTM;
		tilingSprite.y = point.y*game.editor.PTM;

		const angle = Math.atan2(diff.y, diff.x);
		tilingSprite.rotation = angle + 90 * game.editor.DEG2RAD;

		if(placeHook){
			this.anchorTexture.x = previousPoint.x*game.editor.PTM;
			this.anchorTexture.y = previousPoint.y*game.editor.PTM;
			this.anchorTexture.rotation = angle+Math.PI;
		}

		this.anchorTexture.parent.addChildAt(tilingSprite, this.anchorTexture.parent.getChildIndex(this.anchorTexture));
		this.tilingSprites.push(tilingSprite);
	}

	// Which portal of the pair the anchor sits behind, seen from the rider.
	findAnchorPortalFrame(point){
		if(!this.portalPair) return null;
		const {a, b} = this.portalPair;
		for(const [entrance, exit] of [[a, b], [b, a]]){
			if(!entrance?.body || !exit?.body || entrance.destroyed || exit.destroyed) continue;
			const position = entrance.body.GetPosition();
			const distance = signedPortalDistance(
				{x: point.x, y: point.y},
				{x: position.get_x(), y: position.get_y()},
				entrance.body.GetAngle(),
			);
			if(distance < -0.01) return {entrance, exit};
		}
		return null;
	}

	// Where the rope segment from the anchor to its next point crosses the
	// portal plane, with the far side mapped to its real place.
	findRopePortalCrossing(anchor, next){
		const route = this.portalRopeRoute;
		if(route) {
			const visualRoute = this.getPortalRopeVisual(anchor, next);
			return visualRoute?.mode === 'split' ? visualRoute : null;
		}
		const frame = this.findAnchorPortalFrame(anchor);
		if(!frame) return null;
		const {entrance, exit} = frame;
		const position = entrance.body.GetPosition();
		const portalPosition = {x: position.get_x(), y: position.get_y()};
		const angle = entrance.body.GetAngle();
		const anchorDistance = signedPortalDistance({x: anchor.x, y: anchor.y}, portalPosition, angle);
		const nextDistance = signedPortalDistance({x: next.x, y: next.y}, portalPosition, angle);
		if(nextDistance < 0) return null;
		const t = anchorDistance / (anchorDistance - nextDistance);
		const entryPoint = {
			x: anchor.x + (next.x - anchor.x) * t,
			y: anchor.y + (next.y - anchor.y) * t,
		};
		return {
			entrance,
			exit,
			entryPoint,
			exitPoint: mapPointThroughPortal(entryPoint, entrance, exit),
			anchorReal: mapPointThroughPortal({x: anchor.x, y: anchor.y}, entrance, exit),
		};
	}

	getPortalRopeVisual(anchor, gun){
		const route = this.portalRopeRoute;
		if(!route) return null;
		return getPortalRopeVisualRoute({
			anchor: {x: anchor.x, y: anchor.y},
			gun: {x: gun.x, y: gun.y},
			entrance: route.entrance,
			exit: route.exit,
			anchorReal: route.anchorReal,
			rebased: route.rebased,
			guideTangent: route.guideTangent,
			guideInset: PORTAL_ROPE_GUIDE_INSET,
		});
	}

	getPortalRopeGuideCrossing(){
		const route = this.portalRopeRoute;
		if(!route || !Number.isFinite(route.guideTangent) ||
			!route.entrance?.body || !route.exit?.body ||
			route.entrance.destroyed || route.exit.destroyed) return null;
		const position = route.entrance.body.GetPosition();
		const portalPosition = {x: position.get_x(), y: position.get_y()};
		const guide = portalApertureGuidePoint(
			portalPosition,
			route.entrance.body.GetAngle(),
			route.guideTangent,
			route.entrance.apertureHalfLength,
			PORTAL_ROPE_GUIDE_INSET,
		);
		const entryPoint = {x: guide.x, y: guide.y};
		return {
			entrance: route.entrance,
			exit: route.exit,
			entryPoint,
			exitPoint: mapPointThroughPortal(entryPoint, route.entrance, route.exit),
			anchorReal: {...route.anchorReal},
		};
	}

	getPortalRopePhysicalGuide(){
		const crossing = this.getPortalRopeGuideCrossing();
		if(!crossing) return null;
		const normal = portalNormal(crossing.entrance.body.GetAngle());
		return {
			x: crossing.entryPoint.x - normal.x * PORTAL_ROPE_GUIDE_BACK_OFFSET,
			y: crossing.entryPoint.y - normal.y * PORTAL_ROPE_GUIDE_BACK_OFFSET,
		};
	}

	// Catch the rope on a portal lip when its unfolded path would cross the wall,
	// then release it once that direct path has moved safely back into the opening.
	// The release test always uses the original virtual hook rather than the
	// current guide, otherwise a rope touching the lip could never unwrap itself.
	updatePortalRopeGuide(){
		const route = this.portalRopeRoute;
		if(!route || route.rebased ||
			!this.ropeEnd || this.ropeEnd.destroyed ||
			!route.anchorBody || route.anchorBody.destroyed) return;
		const virtualAnchor = mapPointThroughPortal(
			route.anchorReal,
			route.exit,
			route.entrance,
		);
		const gunVector = this.getGunStartPosition();
		const gunPoint = {x: gunVector.get_x(), y: gunVector.get_y()};
		Box2D.destroy(gunVector);
		const crossing = getPortalRopeVisualRoute({
			anchor: virtualAnchor,
			gun: gunPoint,
			entrance: route.entrance,
			exit: route.exit,
			anchorReal: route.anchorReal,
			rebased: false,
			guideTangent: null,
		});
		if(!crossing || crossing.entrance !== route.entrance) return;
		const position = route.entrance.body.GetPosition();
		const portalPosition = {x: position.get_x(), y: position.get_y()};
		const tangentOffset = portalTangentDistance(
			crossing.entryPoint,
			portalPosition,
			route.entrance.body.GetAngle(),
		);
		const nextGuideTangent = resolvePortalApertureGuideTangent(
			tangentOffset,
			route.entrance.apertureHalfLength,
			route.guideTangent,
			PORTAL_ROPE_GUIDE_INSET,
			PORTAL_ROPE_GUIDE_RELEASE_HYSTERESIS,
		);
		const wasGuided = Number.isFinite(route.guideTangent);
		const willBeGuided = Number.isFinite(nextGuideTangent);
		if((!wasGuided && !willBeGuided) ||
			(wasGuided && willBeGuided && nextGuideTangent === route.guideTangent)) return;

		route.guideTangent = nextGuideTangent;
		const anchorPoint = willBeGuided ? this.getPortalRopePhysicalGuide() : virtualAnchor;
		if(!anchorPoint) return;
		const physicalAnchor = new Box2D.b2Vec2(anchorPoint.x, anchorPoint.y);
		this.releaseRope();
		this.attachRope(physicalAnchor, route.anchorBody, true);
		Box2D.destroy(physicalAnchor);
		// Reapply a held reel direction to the newly-created prismatic joint on the
		// next input update.
		this.oldDir = 0;
	}

	// The rider's island, hook body included, was just mapped through a portal.
	// Bends belong to the side the rider left: go straight back to the hook and
	// pin it again where its body now is.
	onCharacterTeleported(){
		if(!this.portalPair || !this.ropeActive || !this.ropeEnd || this.ropeEnd.destroyed) return;
		if(!this.revoluteJoint || this.revoluteJoint.destroyed) return;
		const route = this.portalRopeRoute;
		const candidate = [this.hatBody, this.head, this.body]
			.map(body => body?._portalTransitCandidate)
			.find(Boolean);
		if(route){
			// A live candidate swaps source and target whenever canonical ownership
			// changes. Retain the original route and only change which representation
			// supplies the gun endpoint.
			route.rebased = candidate ? candidate.source === route.exit : true;
		}
		let anchorBody = route?.anchorBody || this.revoluteJoint.GetBodyA();
		let precise = false;
		let anchorPoint;
		if(route?.rebased){
			anchorPoint = new Box2D.b2Vec2(route.anchorReal.x, route.anchorReal.y);
		}else if(route){
			const guidePoint = this.getPortalRopePhysicalGuide();
			const virtualAnchor = guidePoint || mapPointThroughPortal(
				route.anchorReal,
				route.exit,
				route.entrance,
			);
			anchorPoint = new Box2D.b2Vec2(virtualAnchor.x, virtualAnchor.y);
			precise = true;
		}else{
			anchorPoint = b2CloneVec2(this.ropeEnd.GetPosition());
		}
		if(!route && this.ropePoints.length){
			const far = this.ropePoints[0];
			anchorBody = far.body;
			const frame = this.findAnchorPortalFrame(far.point);
			const real = frame ? mapPointThroughPortal({x: far.point.x, y: far.point.y}, frame.entrance, frame.exit) : far.point;
			anchorPoint = new Box2D.b2Vec2(real.x, real.y);
			this.ropePoints = [];
			this.bendRopeLength = 0;
		}
		if(!anchorBody || anchorBody.destroyed) return;
		this.releaseRope();
		// Ownership swaps while part of the articulated rider can still occupy
		// the old side. Keep portal bend suppression until that transit candidate
		// has fully cleared, and place the rebuilt hook just outside its host so
		// its own mounting surface cannot immediately become a false bend.
		this.portalTransitRebased = true;
		this.attachRope(anchorPoint, anchorBody, precise);
		Box2D.destroy(anchorPoint);
		this.oldDir = 0;
	}

	clearRope(){
		if(this.anchorTexture && this.anchorTexture.parent){
			this.anchorTexture.parent.removeChild(this.anchorTexture);
			this.clearTilingRope();
		}
	}

	showRopeAnimation(){
		this.clearTilingRope();

		if(!this.anchorTexture.parent) return;

		const crossing = this.animationCrossing;
		const far = Boolean(crossing) && this.animationPhase === 'far';
		const gunStart = this.getGunStartPosition();
		const current = this.currentAnimationPoint;
		// Retracting on the near side follows the moving gun.
		const target = (!this.ropeGoingOut && !far) ? gunStart : this.targetAnimationPoint;
		const travel = ANIMATION_TRAVEL_SPEED * game.editor.deltaTimeSeconds;
		const dx = target.get_x() - current.get_x();
		const dy = target.get_y() - current.get_y();
		const distance = Math.hypot(dx, dy);
		if(distance > travel){
			current.set_x(current.get_x() + dx / distance * travel);
			current.set_y(current.get_y() + dy / distance * travel);
		}
		const tip = {x: current.get_x(), y: current.get_y()};

		// The rope runs from the gun to the hook, continuing on the far side of
		// a portal once the hook has passed through it.
		if(far){
			this.drawRopeSegment(crossing.entryPoint, gunStart, false);
			this.drawRopeSegment(crossing.exitPoint, tip, false);
		}else{
			this.drawRopeSegment(gunStart, tip, false);
		}
		this.anchorTexture.x = tip.x*game.editor.PTM;
		this.anchorTexture.y = tip.y*game.editor.PTM;
		if(!this.ropeGoingOut){
			const from = far ? crossing.exitPoint : gunStart;
			this.anchorTexture.rotation = Math.atan2(tip.y - from.y, tip.x - from.x);
		}else if(far){
			this.anchorTexture.rotation = this.animationFarAngle;
		}

		if(distance > travel * 1.5) return;

		// Reached the portal: carry on from the linked portal.
		if(crossing && this.ropeGoingOut && this.animationPhase === 'near'){
			this.animationPhase = 'far';
			this.currentAnimationPoint = new Box2D.b2Vec2(crossing.exitPoint.x, crossing.exitPoint.y);
			this.targetAnimationPoint = new Box2D.b2Vec2(crossing.anchorReal.x, crossing.anchorReal.y);
			return;
		}
		if(crossing && !this.ropeGoingOut && this.animationPhase === 'far'){
			this.animationPhase = 'near';
			this.currentAnimationPoint = new Box2D.b2Vec2(crossing.entryPoint.x, crossing.entryPoint.y);
			return;
		}

		if(this.ropeGoingOut && !this.ropeAttached){
			AudioManager.playSFX('rope-in', 0.2, 1.0 + 0.4 * Math.random()-0.2, this.hatBody.GetPosition());
			if(crossing && far){
				// A missed hook has a real outbound segment beyond the exit. Reel that
				// segment back to the linked portal before bringing the near segment home.
				this.currentAnimationPoint = new Box2D.b2Vec2(
					crossing.anchorReal.x,
					crossing.anchorReal.y,
				);
				this.targetAnimationPoint = new Box2D.b2Vec2(
					crossing.exitPoint.x,
					crossing.exitPoint.y,
				);
			}
		}else{
			this.showAnimation = false;

			if(!this.ropeAttached){
				this.clearRope();
				this.anchorTexture = null;
				this.animationCrossing = null;
				AudioManager.playSFX('rope-helmet', 0.2, 1.0 + 0.4 * Math.random()-0.2, this.hatBody.GetPosition());
			}else{
				AudioManager.playSFX('rope-ground', 0.3, 1.0 + 0.4 * Math.random()-0.2, this.hatBody.GetPosition());
			}

		}
		this.ropeGoingOut = false;
	}

	lean(dir) {
		if (!this.revoluteJoint) return;
		// A rope threaded through a finite portal is guided by that opening, not
		// free to orbit its virtual anchor through the surrounding wall. Suppress
		// the Rope Hat's lateral swing impulse until the connected island has fully
		// emerged and portalPair is cleared; the portal caps remain the physical
		// fit test for the rider and vehicle.
		if(this.portalPair && !this.ropePoints.length) return;

		const angle = this.body.GetAngle();

		let baseForce = this.character.attachedToVehicle && this.character.mainPrefabClass.lookupObject['frame'] ? 100 : 20;

		const force = baseForce * dir;
		const xForce = force * Math.cos(angle);
		const yForce = force * Math.sin(angle);

		const forceVec = vec1;
		forceVec.Set(xForce, yForce);

		this.body.ApplyForce(forceVec, this.body.GetPosition(), true);
	}
	accelerate(dir) {
		if (!this.pulleyJoint) return;

		const ropeDiss = vec1;
		ropeDiss.Set(this.ropeEnd.GetPosition().x, this.ropeEnd.GetPosition().y);
		b2SubVec2(ropeDiss, this.hatBody.GetPosition());
		const ropeLength = ropeDiss.Length();
		// Stop pulling when we are below min length. A rope through a portal keeps
		// reeling until the rider has fully come out on the far side: the anchor
		// may sit only a few metres beyond the exit, and the moment ownership
		// transfers the rope is measured on that side while the rider is still
		// inside the exit's wall. The minimum applies once the transit is over.
		const throughPortal = Boolean(this.portalRopeRoute) && !this.ropePoints.length;
		const inTransit = Boolean(this.character?.isPortalTransitActive?.());
		if(dir<0 && ropeLength<this.minRopeLength && !throughPortal && !inTransit){
			dir = 0;
		}

		if(this.oldDir == dir) return;
		if(dir === 0){
			this.pulleyJoint.EnableMotor(false);
			if(this.pulleyFrameJoint) this.pulleyFrameJoint.EnableMotor(false);
			this.setDistanceJointEnabled(true);
			this.oldDir = dir;
			return;
		}
		this.setDistanceJointEnabled(false);

		const speed = 5;
		this.pulleyJoint.EnableMotor(true);
		this.pulleyJoint.SetMotorSpeed(speed*-dir);
		if(this.pulleyFrameJoint){
			this.pulleyFrameJoint.EnableMotor(true);
			this.pulleyFrameJoint.SetMotorSpeed(speed*-dir);
		}
		this.oldDir = dir;
	}
	flip(){
		this.detachRope();
		super.flip();
	}

	detach(){
		super.detach();
		this.detachRope();
		this.clearRope();
	}

	detachFromVehicle(){
		if(this.frameJoint)	game.editor.DestroyJoint(this.frameJoint);
		if(this.pulleyFrameJoint) game.editor.DestroyJoint(this.pulleyFrameJoint);
		this.frameJoint = this.pulleyFrameJoint = null;
		if(this.ropeFired) this.setDistanceJointEnabled(true);
	}

	update() {
		if(this.portalTransitRebased && !this.character?.isPortalTransitActive?.()){
			// Both rope ends now live entirely in the destination frame. From this
			// point onward normal bend collision and straight-line rendering apply.
			this.portalTransitRebased = false;
			this.portalPair = null;
			this.portalRopeRoute = null;
			this.animationCrossing = null;
			this.animationPhase = 'near';
		}
		if(this.showAnimation){
			this.showRopeAnimation();
		}

		if (this.ropeActive) {
			if(!this.showAnimation){
				this.updatePortalRopeGuide();
				this.updateRopeFixture();
			}
			if (this.bendBody) {
				this.bendRope(this.bendPoint, this.bendBody);
			}
			// detect unbend
			if (this.ropePoints.length > 0) {
				const lastPoint = this.ropePoints[this.ropePoints.length - 1];
				const savedSpeed = lastPoint.speed;
				const currentSpeed = this.revoluteJoint.GetJointSpeed();

				const x2 = lastPoint.point.x;
				const y2 = lastPoint.point.y;
				const x1 = this.ropeEnd.GetPosition().x;
				const y1 = this.ropeEnd.GetPosition().y;
				const x = this.head.GetPosition().x;
				const y = this.head.GetPosition().y;

				if (currentSpeed < 0 && savedSpeed > 0) {
					if (((x2 - x1) * (y - y1) - (y2 - y1) * (x - x1)) > 0) {
						this.unBendRope();
					}
				} else if (currentSpeed > 0 && savedSpeed < 0) {
					if (((x2 - x1) * (y - y1) - (y2 - y1) * (x - x1)) < 0) {
						this.unBendRope();
					}
				}
			}
		}
	}
}
